// WhatsApp-style text formatting, typed as plain text (no toolbar):
//
//   *bold*   _italic_   ~strikethrough~   ```monospace```   `inline code`
//   > quote          (a line starting with "> ")
//   * item / - item  (bulleted list)
//   1. item          (numbered list)
//
// Like WhatsApp, an inline marker only counts at a word boundary: it opens
// after a space / punctuation / the start and before a non-space, and closes
// after a non-space and before a space / punctuation / the end, on the same
// line. So snake_case_names and 2*3*4 stay as they are. Links and @mentions
// are left alone (markers inside them don't count).
//
// Rendering works on offsets into the original text, so mentions (stored as
// offsets) and links are drawn by the caller's `leaf` for each plain stretch.
import type { JSX } from "preact";

type Node = string | JSX.Element;
/** Draws body[start, end) as plain text (mentions, links). */
export type Leaf = (start: number, end: number) => Node[];

const MARKS: Record<string, "b" | "i" | "s"> = { "*": "b", _: "i", "~": "s" };
const boundary = (c: string | undefined) => c === undefined || /[\s\p{P}\p{S}]/u.test(c);

/** Format `text`; `protect` are ranges (links, mentions) where markers
 * don't count. */
export function formatText(text: string, leaf: Leaf, protect: [number, number][] = []): Node[] {
  const out: Node[] = [];
  // ```monospace``` blocks first: they may span lines and nothing inside counts.
  let pos = 0;
  let key = 0;
  for (const m of text.matchAll(/```([\s\S]+?)```/g)) {
    const a = m.index!;
    if (inside(protect, a)) continue;
    if (a > pos) out.push(...blocks(text, pos, a, leaf, protect, () => key++));
    out.push(
      <code key={"m" + key++} class="fmt-mono">
        {m[1]}
      </code>,
    );
    pos = a + m[0].length;
  }
  if (pos < text.length) out.push(...blocks(text, pos, text.length, leaf, protect, () => key++));
  return out;
}

const inside = (ranges: [number, number][], i: number) => ranges.some(([a, b]) => i >= a && i < b);

type Line = { start: number; end: number; kind: "quote" | "ul" | "ol" | "text"; content: number; n?: number };

/** Lines into quotes, lists and plain lines. */
function blocks(text: string, from: number, to: number, leaf: Leaf, protect: [number, number][], key: () => number): Node[] {
  const lines: Line[] = [];
  let s = from;
  while (s <= to) {
    let e = text.indexOf("\n", s);
    if (e === -1 || e > to) e = to;
    const l = text.slice(s, e);
    let m: RegExpExecArray | null;
    if ((m = /^> /.exec(l))) lines.push({ start: s, end: e, kind: "quote", content: s + 2 });
    else if ((m = /^[*-] (?=\S)/.exec(l))) lines.push({ start: s, end: e, kind: "ul", content: s + 2 });
    else if ((m = /^(\d{1,3})\. (?=\S)/.exec(l))) lines.push({ start: s, end: e, kind: "ol", content: s + m[0].length, n: Number(m[1]) });
    else lines.push({ start: s, end: e, kind: "text", content: s });
    if (e === to) break;
    s = e + 1;
  }
  const out: Node[] = [];
  for (let i = 0; i < lines.length; ) {
    const l = lines[i];
    if (l.kind === "text") {
      out.push(...inline(text, l.content, l.end, leaf, protect));
      if (i < lines.length - 1) out.push("\n");
      i++;
      continue;
    }
    // A run of the same kind of line: one quote / list.
    let j = i;
    while (j < lines.length && lines[j].kind === l.kind) j++;
    const run = lines.slice(i, j);
    if (l.kind === "quote")
      out.push(
        <blockquote key={"q" + key()} class="fmt-quote">
          {run.flatMap((x, k) => [...inline(text, x.content, x.end, leaf, protect), ...(k < run.length - 1 ? ["\n"] : [])])}
        </blockquote>,
      );
    else {
      const items = run.map((x, k) => <li key={k}>{inline(text, x.content, x.end, leaf, protect)}</li>);
      out.push(
        l.kind === "ul" ? (
          <ul key={"u" + key()} class="fmt-list">
            {items}
          </ul>
        ) : (
          <ol key={"o" + key()} class="fmt-list" start={l.n}>
            {items}
          </ol>
        ),
      );
    }
    i = j;
  }
  return out;
}

/** Inline formatting within [from, to) (one line). */
function inline(text: string, from: number, to: number, leaf: Leaf, protect: [number, number][]): Node[] {
  const out: Node[] = [];
  let plain = from;
  let i = from;
  while (i < to) {
    const c = text[i];
    const tag = c === "`" ? "code" : MARKS[c];
    if (tag && !inside(protect, i) && boundary(text[i - 1]) && text[i - 1] !== c && i + 1 < to && /\S/.test(text[i + 1]) && text[i + 1] !== c) {
      // The nearest closer on this line.
      let j = i + 2;
      for (; j < to; j++)
        if (text[j] === c && !inside(protect, j) && /\S/.test(text[j - 1]) && (j + 1 >= to || boundary(text[j + 1])) && text[j + 1] !== c) break;
      if (j < to) {
        if (i > plain) out.push(...leaf(plain, i));
        const k = out.length;
        if (tag === "code")
          out.push(
            <code key={"c" + k} class="fmt-code">
              {text.slice(i + 1, j)}
            </code>,
          );
        else {
          const kids = inline(text, i + 1, j, leaf, protect);
          out.push(tag === "b" ? <strong key={"b" + k}>{kids}</strong> : tag === "i" ? <em key={"i" + k}>{kids}</em> : <s key={"s" + k}>{kids}</s>);
        }
        i = plain = j + 1;
        continue;
      }
    }
    i++;
  }
  if (plain < to) out.push(...leaf(plain, to));
  return out;
}

/** Wrap the textarea's selection in a marker (Ctrl+B / I / Shift+X / Shift+M),
 * or unwrap it. The field is updated here (so the re-render finds nothing
 * to change and the selection stays); returns the new text for the state. */
export function wrapSelection(el: HTMLTextAreaElement, marker: string): string {
  const { selectionStart: a, selectionEnd: b, value } = el;
  const sel = value.slice(a, b);
  const n = marker.length;
  const wrapped = value.slice(a - n, a) === marker && value.slice(b, b + n) === marker;
  el.value = wrapped ? value.slice(0, a - n) + sel + value.slice(b + n) : value.slice(0, a) + marker + sel + marker + value.slice(b);
  if (wrapped) el.setSelectionRange(a - n, b - n);
  else el.setSelectionRange(a + n, b + n);
  return el.value;
}
